import type { ReactNode } from 'react';
import React, { useCallback } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils/entry-points/useIsWhiteboardFeatureEnabled.ts';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { canPerformSpaceOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';

import { ImportOnboardingCTA } from '@confluence/experiment-cfind-5926-upload-in-onboarding/entry-points/ImportOnboardingCTA.ts';
import { createPageContent } from '../CreateContent/dataFunctions.ts';

import type {
	FooterCreateButtonsQuery$data,
	FooterCreateButtonsQuery$variables,
} from './__generated__/FooterCreateButtonsQuery.graphql.ts';

const FooterCreateButtonsQuery = graphql`
	query FooterCreateButtonsQuery($spaceKey: String) {
		space(key: $spaceKey) {
			id
			key
			spaceTypeSettings {
				enabledContentTypes {
					isLivePagesEnabled
					isWhiteboardsEnabled
					isDatabasesEnabled
				}
			}
			didContainUserContent
			operations {
				operation
				targetType
			}
		}
	}
`;

const ContentTreeOnboardingLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ContentTreeOnboarding" */ './ContentTreeOnboarding.tsx'
			)
		).ContentTreeOnboarding,
});

const TreeBottomCreateButtonLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-TreeBottomCreateButton" */ './TreeBottomCreateButton.tsx'
			)
		).TreeBottomCreateButton,
});

type FooterCreateButtonsProps = {
	spaceKey: string;
	isContentTreeLoading: boolean;
	emptyContent?: ReactNode;
};

export const FooterCreateButtons = ({
	spaceKey,
	isContentTreeLoading,
	emptyContent,
}: FooterCreateButtonsProps) => {
	const isDatabasesGloballyEnabled = isContentTypeEnabledInCurrentEnv('database');
	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const { push } = useRouteActions();

	const handleImportCTAClick = useCallback(async () => {
		const { url } = await createPageContent({
			spaceKey,
			type: 'page',
			shouldOpenImportTab: true,
		});
		if (url) {
			push(url);
		}
	}, [spaceKey, push]);

	const { data, loading, error } = useQueryInterop<
		FooterCreateButtonsQuery$data,
		FooterCreateButtonsQuery$variables
	>(FooterCreateButtonsQuery, {
		errorPolicy: 'all',
		variables: {
			spaceKey,
		},
	});

	if (isContentTreeLoading || loading || !data) {
		return null;
	}

	const canCreatePage = canPerformSpaceOperation({
		operationCheckResult: data,
		operation: OperationTypes.CREATE,
		contentType: 'page',
	});

	if (!canCreatePage) {
		return <>{emptyContent}</>;
	}

	const didContainUserContent = data?.space?.didContainUserContent;

	const showContentTreeOnboarding = didContainUserContent === false;
	const showLiveDoc = Boolean(
		data?.space?.spaceTypeSettings?.enabledContentTypes?.isLivePagesEnabled,
	);
	const showWhiteboard =
		isWhiteboardFeatureEnabled('whiteboardsEnabled') &&
		Boolean(data?.space?.spaceTypeSettings?.enabledContentTypes?.isWhiteboardsEnabled);
	const showDatabase =
		isDatabasesGloballyEnabled &&
		Boolean(data?.space?.spaceTypeSettings?.enabledContentTypes?.isDatabasesEnabled);

	return (
		<>
			{showContentTreeOnboarding ? (
				<ContentTreeOnboardingLoader
					showLiveDoc={showLiveDoc}
					showWhiteboard={showWhiteboard}
					showDatabase={showDatabase}
				/>
			) : (
				emptyContent || (
					<>
						<TreeBottomCreateButtonLoader
							spaceKey={spaceKey}
							source="endOfContentTreeContextualCreate"
						/>
						<ImportOnboardingCTA onClick={handleImportCTAClick} />
					</>
				)
			)}
			{error && <ErrorDisplay error={error} />}
		</>
	);
};
